import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { fr } from "../i18n/fr";

/** Server reachability, provided by the shell (SW-SY-10, SW-SY-11): forms disable their writes while offline. */
export const OfflineContext = createContext(false);
export const useOffline = () => useContext(OfflineContext);

/**
 * Native dialog, closed by Escape or the backdrop. `side` dialogs are not modal: they dock to the right so that
 * the map stays visible and usable behind them (import preview, SW-IM-03).
 */
export function Dialog({ open, title, onClose, children, wide, side }: { open: boolean; title: string; onClose(): void; children: ReactNode; wide?: boolean; side?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  // `side` toggled while open: the dialog is re-opened in the other mode and the resulting close event is swallowed
  const switching = useRef(false);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && d.open) { switching.current = true; d.close(); }
    if (open) { if (side) d.show(); else d.showModal(); }
    if (!open && d.open) d.close();
  }, [open, side]);
  return (
    <dialog ref={ref} className={"dialog" + (wide ? " wide" : "") + (side ? " side" : "")}
      onClose={() => { if (switching.current) { switching.current = false; return; } onClose(); }}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}>
      {open && (
        <div className="dialog-body" onClick={(e) => e.stopPropagation()}>
          <div className="dialog-head"><h2>{title}</h2><button className="icon" aria-label={fr.common.close} onClick={onClose}>×</button></div>
          {children}
        </div>
      )}
    </dialog>
  );
}

export function Field({ label, children, hint, inline }: { label: ReactNode; children: ReactNode; hint?: ReactNode; inline?: boolean }) {
  return (
    <label className={"field" + (inline ? " inline" : "")}>
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

export function Notice({ kind = "info", children, onClose }: { kind?: "info" | "error" | "warn" | "ok"; children: ReactNode; onClose?(): void }) {
  return <div className={"notice " + kind}>{children}{onClose && <button className="icon" onClick={onClose} aria-label={fr.common.close}>×</button>}</div>;
}

/** Transient message at the bottom of the screen. */
export function useToast() {
  const [toast, setToast] = useState<{ text: string; kind: "ok" | "error" } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.kind === "error" ? 8000 : 3500);
    return () => clearTimeout(t);
  }, [toast]);
  const el = toast ? <div className={"toast " + toast.kind} role="status">{toast.text}</div> : null;
  return { show: (text: string, kind: "ok" | "error" = "ok") => setToast({ text, kind }), el };
}

export function Confirm({ open, title, text, confirmLabel, danger, onConfirm, onClose, children }: {
  open: boolean; title: string; text?: ReactNode; confirmLabel?: string; danger?: boolean; onConfirm(): void | Promise<void>; onClose(): void; children?: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} title={title} onClose={onClose}>
      {text && <p>{text}</p>}
      {children}
      <div className="row end">
        <button onClick={onClose}>{fr.common.cancel}</button>
        <button className={danger ? "danger" : "primary"} disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}>{confirmLabel ?? fr.common.confirm}</button>
      </div>
    </Dialog>
  );
}

export function StatusBadge({ status }: { status: string }) {
  return <span className={"badge ev-" + status}>{fr.events.status[status] ?? status}</span>;
}

/** Phase of the event against the clock (SW-EV-01): upcoming, ongoing, ended. */
export function PhaseBadge({ phase }: { phase: string }) {
  return <span className={"badge ph-" + phase}>{fr.events.phase[phase] ?? phase}</span>;
}

export function Spinner({ label }: { label?: string }) {
  return <span className="muted small">{label ?? fr.common.loading}</span>;
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={async () => { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? fr.common.copied : label ?? fr.common.copy}
    </button>
  );
}

/** Logo of Secours Nav, the icon of the Android application: orange navigation arrow on blue (E1). */
export function Logo({ size = 32, className = "" }: { size?: number; className?: string }) {
  return <img src="/favicon.svg" alt="" width={size} height={size} className={("logo " + className).trim()} />;
}

/** `ABCDEFGH` shown `ABCD EFGH`: event identifier and access key, easier to copy by hand (DR17). */
export const groups = (v: string) => (v.length === 8 ? `${v.slice(0, 4)} ${v.slice(4)}` : v);
